import React, { useMemo } from "react";

import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";

import { APP_NAME } from "Constants/documentationUrls";
import Button from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentsAsArraySelector } from "Reducers/environment";
import {
  blackfireEnabledInEnvironmentSelector,
  blackfireUUIDSelector
} from "Reducers/environment/deployment";
import { organizationProfileSelector } from "Reducers/organization/profile";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import { useAppSelector } from "Store/hooks";

import * as S from "./Specificities.styles";

const Blackfire = ({ integration }) => {
  const intl = useIntl();

  const { environmentId, organizationId, projectId } = useDecodedParams();
  const observabilitySuiteTier = ["enterprise", "elite"];

  const formatMessage = useIntl().formatMessage;

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);

  const mainEnvironmentId = useMemo(
    () => environments?.find(env => env.is_main)?.id,
    [environments]
  );

  const hasObservabilitySuite = (tier = "", isDg2) =>
    observabilitySuiteTier.includes(tier.toLowerCase()) || isDg2;

  const isBlackfireEnabledInParentEnvironment = useAppSelector(state =>
    blackfireEnabledInEnvironmentSelector(state, {
      organizationId,
      projectId,
      environmentId: mainEnvironmentId
    })
  );

  const isBlackfireEnabledInEnvironment = useAppSelector(state =>
    blackfireEnabledInEnvironmentSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );

  const blackFireIntegration = true;

  const accountTier = useAppSelector(state =>
    organizationProfileSelector(state, { organizationId })
  )?.account_tier;

  const isDg2 = environments?.some(
    ({ deployment_target }) =>
      deployment_target === "enterprise" || deployment_target === "dedicated"
  );

  const isObservabilitySuite = hasObservabilitySuite(accountTier, isDg2);

  const uuid = useAppSelector(state =>
    blackfireUUIDSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const hasBlackFireIntegration =
    blackFireIntegration || capabilites?.blackfire?.enabled;

  const environmentCredentials =
    blackFireIntegration?.environments_credentials?.[
      environmentId || mainEnvironmentId
    ];

  const redirectURL = useMemo(() => {
    if (isBlackfireEnabledInEnvironment || hasBlackFireIntegration) {
      const serverUUID = uuid || environmentCredentials?.server_uuid;
      return `${formatMessage({ id: "links.blackfire.connect" })}${
        serverUUID ? `?target=/agents/${serverUUID}` : ""
      }`;
    }
    if (isBlackfireEnabledInParentEnvironment)
      return formatMessage({
        id: "links.observability-suite-blackfire.integrate"
      });
    if (isObservabilitySuite)
      return formatMessage({ id: "links.observability-suite-features" });
    return formatMessage({ id: "links.blackfire.features" });
  }, [
    isBlackfireEnabledInEnvironment,
    isBlackfireEnabledInParentEnvironment,
    hasBlackFireIntegration,
    uuid,
    formatMessage,
    isObservabilitySuite,
    environmentCredentials?.server_uuid
  ]);

  let actionButtonTextId = useMemo(() => {
    if (isBlackfireEnabledInEnvironment || hasBlackFireIntegration) {
      return "blackfire.card.cta.launch";
    }
    return "blackfire.card.cta.learn_more";
  }, [isBlackfireEnabledInEnvironment, hasBlackFireIntegration]);

  const getTags = kind => {
    if (!integration[kind]?.length) return;

    return (
      <p key={`l-${kind}`}>
        <label>
          {intl.formatMessage({
            id: `integration.custom.${kind}`,
            defaultMessage: kind
          })}
        </label>
        {integration[kind].map((elt, i) => (
          <S.Tag key={`${kind}-${i}`}>{elt}</S.Tag>
        ))}
      </p>
    );
  };

  return (
    <>
      <S.ButtonWrapper>
        <Button
          as="a"
          href={redirectURL}
          analyticId={actionButtonTextId}
          target="_blank"
        >
          <FormattedMessage id={actionButtonTextId} />
        </Button>
      </S.ButtonWrapper>
      <S.MonitorWrapper>
        <p>
          {intl.formatMessage(
            {
              id: "integration.blackfire.description",
              defaultMessage:
                "Monitor, validate and improve site performance with Blackfire, a Platform.sh product. Learn more."
            },
            { appName: APP_NAME }
          )}
          <a
            href={intl.formatMessage({
              id: "links.observability-suite-blackfire"
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage({
              id: "integration.blackfire.url",
              defaultMessage: " Learn more."
            })}
          </a>
        </p>
      </S.MonitorWrapper>
      <S.Wrapper>
        {[
          "events",
          "states",
          "environments",
          "excluded_environments",
          "supported_runtimes"
        ].map(kind => getTags(kind))}
      </S.Wrapper>
    </>
  );
};

Blackfire.propTypes = {
  integration: PropTypes.object
};

export default Blackfire;
